import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { stats, type Organization } from '../../api/stats';
import { authClientFor, identityPath, loadTargets, useTargets } from '../../identity/session';
import type { AliasInput, Customer, Tenant } from '../../identity/api';
import { loadAuthDirectory, resetStatsLinks, statsConfig } from './links';
import { IngestStatus, orgPath } from './shared';
import './statistics.css';

const CREATE = '__create__';
const SKIP = '';
const TENANT = 'tenant:';

const pmsOf = (o: { aliases?: { system: string; value: string }[] | null }) =>
  (o.aliases ?? []).filter((a) => a.system === 'pms').map((a) => a.value);

/**
 * AUTH resolves a bearer's PMS customer from its tenant's `pms` alias, else from its customer's single `pms` alias
 * (several on the customer = ambiguous, rejected). Customers that would hold more than one need the ids on the
 * installations' tenants instead.
 */
export function ambiguousCustomers(customers: Customer[], tenants: Tenant[]) {
  return customers
    .filter((c) => pmsOf(c).length > 1)
    .map((c) => ({
      customer: c,
      ids: pmsOf(c),
      tenantsWithout: tenants.filter((t) => t.customerId === c.id && !pmsOf(t).length),
    }))
    .filter((x) => x.tenantsWithout.length > 0);
}

/** Lower-case letters and digits only, without legal-form suffixes: "Acme Logistics AB" → "acmelogistics". */
export function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\b(ab|as|a\/s|oy|gmbh|bv|ltd|inc|llc|aps|sa|nv)\b\.?/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/** The AUTH customer whose name matches the organization's best: same normalized name, else one containing the other. */
export function suggestCustomer(org: Pick<Organization, 'name'>, customers: Customer[]): Customer | null {
  const n = normalizeName(org.name ?? '');
  if (!n) return null;
  const exact = customers.find((c) => normalizeName(c.name) === n);
  if (exact) return exact;
  const partial = customers.filter((c) => {
    const m = normalizeName(c.name);
    return m.length >= 3 && (m.includes(n) || n.includes(m));
  });
  return partial.length === 1 ? partial[0] : null;
}

async function allOrganizations(): Promise<Organization[]> {
  const out: Organization[] = [];
  for (let offset = 0; offset < 10_000; offset += 500) {
    const page = await stats.organizations({ offset, limit: 500 });
    out.push(...page.items);
    if (!page.hasMore) break;
  }
  return out;
}

/**
 * "Import from PMS": every collector organization (PMS customer id) without a `pms` alias in AUTH, matched to an AUTH
 * customer by name or created as a new one; linking sets the alias `pms:<customerId>`. Uses the hub's own AUTH (the
 * one the collector trusts) through the identity proxy, so every change is audited there.
 */
export function ImportPmsPage() {
  const { signedIn } = useTargets();
  const [target, setTarget] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    void Promise.all([statsConfig(), loadTargets()]).then(([c]) => setTarget(c.authTarget));
  }, []);
  const ready = !!target && signedIn(target);
  const data = useAsync(
    async () => (ready ? { orgs: await allOrganizations(), dir: await loadAuthDirectory(target!) } : null),
    [ready, target]
  );
  const [choice, setChoice] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const toast = useNotice();

  const customers = data.data?.dir.customers ?? [];
  const tenants = data.data?.dir.tenants ?? [];
  const customerName = (id: string) => customers.find((c) => c.id === id)?.name ?? id;
  const linked = new Set([...customers.flatMap(pmsOf), ...tenants.flatMap(pmsOf)]);
  const ambiguous = ambiguousCustomers(customers, tenants);
  const pending = (data.data?.orgs ?? []).filter((o) => !linked.has(String(o.customerId)));
  const suggestion = (o: Organization) => suggestCustomer(o, customers);
  const chosen = (o: Organization) => choice[o.customerId] ?? suggestion(o)?.id ?? SKIP;
  const options = (o: Organization) => {
    const s = suggestion(o);
    return [
      { value: SKIP, label: '— choose —' },
      ...(s ? [{ value: s.id, label: `${s.name} (name match)` }] : []),
      { value: CREATE, label: `Create customer “${o.name || `Customer ${o.customerId}`}”` },
      ...customers
        .filter((c) => c.id !== s?.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ value: c.id, label: c.name })),
      ...[...tenants]
        .filter((t) => t.status !== 'Deleted')
        .sort(
          (a, b) =>
            customerName(a.customerId).localeCompare(customerName(b.customerId)) ||
            a.name.localeCompare(b.name)
        )
        .map((t) => ({ value: TENANT + t.id, label: `Tenant ${t.name} · ${customerName(t.customerId)}` })),
    ];
  };
  /** Why linking this organization to the chosen customer would make bearers of its tenants ambiguous. */
  const conflict = (o: Organization): string | null => {
    const value = chosen(o);
    const c = customers.find((x) => x.id === value);
    if (!c) return null;
    const others = [
      ...pmsOf(c),
      ...pending.filter((p) => p !== o && chosen(p) === c.id).map((p) => String(p.customerId)),
    ];
    return others.length
      ? `${c.name} would hold pms:${[o.customerId, ...others].join(', pms:')}: AUTH bearers of its tenants then cannot ` +
          'tell which PMS customer they are. Link each PMS id to the installation’s tenant instead.'
      : null;
  };

  // The alias PUT replaces the whole list: track what each customer holds while several organizations are linked.
  const link = async (o: Organization, held: Map<string, AliasInput[]>) => {
    const client = authClientFor(target!);
    const value = chosen(o);
    if (value.startsWith(TENANT)) {
      const tenant = tenants.find((t) => TENANT + t.id === value);
      if (!tenant) return;
      const keep =
        held.get(value) ?? (tenant.aliases ?? []).map(({ system, value: v }) => ({ system, value: v }));
      const next = [...keep, { system: 'pms', value: String(o.customerId) }];
      await client.tenants.aliases.replace(tenant.id, next);
      held.set(value, next);
      return;
    }
    let customer = customers.find((c) => c.id === value);
    if (value === CREATE)
      customer = await client.customers.create({ name: o.name || `Customer ${o.customerId}` });
    if (!customer) return;
    const keep =
      held.get(customer.id) ?? (customer.aliases ?? []).map(({ system, value: v }) => ({ system, value: v }));
    const next = [...keep, { system: 'pms', value: String(o.customerId) }];
    await client.customers.aliases.replace(customer.id, next);
    held.set(customer.id, next);
  };
  const run = async (list: Organization[], key: number | 'all') => {
    setBusy(key);
    let done = 0;
    const held = new Map<string, AliasInput[]>();
    try {
      for (const o of list) {
        await link(o, held);
        done++;
      }
      toast.success(`Linked ${done} ${done === 1 ? 'organization' : 'organizations'}`);
    } catch (e) {
      toast.error(`Linked ${done}; then: ${(e as Error).message}`);
    } finally {
      setBusy(null);
      resetStatsLinks();
      void data.reload();
    }
  };
  const suggested = pending.filter((o) => chosen(o) !== SKIP);
  const conflicts = suggested.filter((o) => conflict(o)).length;

  return (
    <Page
      title="Import from PMS"
      description="Link every collector organization (PMS customer id) to an AUTH customer with the alias pms:<id>, so statistics, identity and environments meet."
      actions={
        ready &&
        suggested.length > 0 && (
          <ConfirmButton
            loading={busy === 'all'}
            disabled={busy !== null}
            message={
              conflicts
                ? `${conflicts} of them would leave a customer with several pms aliases (ambiguous for AUTH bearers). Link anyway?`
                : `Link ${suggested.length} organizations?`
            }
            onConfirm={() => void run(suggested, 'all')}
          >
            Link {suggested.length} chosen
          </ConfirmButton>
        )
      }
    >
      {toast.element}
      {target === null && (
        <EmptyState
          card
          title="The hub’s AUTH is not one of its AuthTargets."
          hint="Add it to the AuthTargets setting."
        />
      )}
      {target && !ready && (
        <EmptyState
          card
          title={`Not signed in to AUTH ${target}`}
          action={
            <Link className="nyk-link" to={identityPath('/identity/customers', target)}>
              Sign in on the Customers page
            </Link>
          }
        />
      )}
      <ErrorBox error={data.error} />
      <Loading loading={ready && data.loading && !data.data} />
      {data.data && ambiguous.length > 0 && (
        <div className="nyk-notice nyk-notice-warn" data-testid="import-ambiguous">
          <b>Ambiguous for AUTH bearers.</b> These customers hold several <code>pms</code> aliases, so a
          tenant without its own <code>pms</code> alias cannot be mapped to a PMS customer (ingest rejects its
          bearer):
          <ul>
            {ambiguous.map((a) => (
              <li key={a.customer.id}>
                <Link className="nyk-link" to={identityPath('/identity/customers', target!)}>
                  {a.customer.name}
                </Link>{' '}
                (pms:{a.ids.join(', pms:')}) — tenants without one:{' '}
                {a.tenantsWithout.map((t, i) => (
                  <span key={t.id}>
                    {i > 0 && ', '}
                    <Link
                      className="nyk-link"
                      to={identityPath(`/identity/tenants/${t.id}?tab=aliases`, target!)}
                    >
                      {t.name}
                    </Link>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.data && (
        <div className="nyk-card">
          <div className="nyk-card-head">
            <h2>Organizations without a pms alias</h2>
            <span className="nyk-ui-hint">
              {pending.length} of {data.data.orgs.length} · AUTH {target} has {customers.length} customers
            </span>
          </div>
          {!pending.length ? (
            <EmptyState title="Every organization is linked." />
          ) : (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="import-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Status</th>
                    <th>AUTH customer</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pending.map((o) => (
                    <tr key={o.customerId} data-org={o.customerId}>
                      <td className="nyk-card-title">
                        <Link className="nyk-link" to={orgPath(o.customerId)}>
                          {o.name || `Customer ${o.customerId}`}
                        </Link>
                        <div className="nyk-ui-hint">pms:{o.customerId}</div>
                      </td>
                      <td data-label="Status">
                        <IngestStatus status={o.status} monitoring={o.active} />
                      </td>
                      <td data-label="AUTH customer">
                        <Select
                          aria-label={`AUTH customer for ${o.customerId}`}
                          value={chosen(o)}
                          onChange={(v) => setChoice((c) => ({ ...c, [o.customerId]: v }))}
                          options={options(o)}
                        />
                        {conflict(o) && (
                          <div
                            className="nyk-notice nyk-notice-warn nyk-notice-sm"
                            data-testid="import-conflict"
                          >
                            {conflict(o)}
                          </div>
                        )}
                      </td>
                      <td className="nyk-row-actions">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={chosen(o) === SKIP || busy !== null}
                          loading={busy === o.customerId}
                          onClick={() => void run([o], o.customerId)}
                        >
                          Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
        </div>
      )}
    </Page>
  );
}
